import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@chainlit/react-client';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Ban, Loader2, Mail, RotateCcw, Search, Shield, UserMinus, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

interface UserAccess {
  id: number;
  email: string;
  can_access: boolean;
  banned?: boolean;
  banned_at?: string | null;
  banned_reason?: string | null;
  banned_by?: string | null;
}

interface PendingAction {
  email: string;
  type: 'revoke' | 'ban' | 'unban';
}

const AdminUserManagement = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [activeAdmins, setActiveAdmins] = useState<UserAccess[]>([]);
  const [allUsers, setAllUsers] = useState<UserAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [banSearch, setBanSearch] = useState('');
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentEmail = user?.identifier?.toLowerCase() ?? '';
  const bannedUsers = allUsers.filter((entry) => entry.banned);
  const adminSuggestions = newEmail.trim()
    ? allUsers
        .filter(
          (entry) =>
            !entry.can_access &&
            !entry.banned &&
            entry.email.toLowerCase().includes(newEmail.trim().toLowerCase())
        )
        .slice(0, 5)
    : [];
  const banSearchResults = banSearch.trim()
    ? allUsers
        .filter(
          (entry) =>
            !entry.banned &&
            entry.email.toLowerCase().includes(banSearch.trim().toLowerCase())
        )
        .slice(0, 8)
    : [];

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/user-access/list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error('Failed to load access list');

      const users = data.data || [];
      setAllUsers(users);
      setActiveAdmins(
        users.filter((entry: UserAccess) => entry.can_access && !entry.banned)
      );
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error(t('admin.admins.errors.load', 'Failed to load access settings'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addAdmin = async (event: React.FormEvent) => {
    event.preventDefault();
    const email = newEmail.trim().toLowerCase();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      toast.error(t('admin.admins.errors.validEmail', 'Enter a valid email address'));
      return;
    }
    if (activeAdmins.some((entry) => entry.email.toLowerCase() === email)) {
      toast.error(t('admin.admins.errors.alreadyAdmin', 'This user already has admin access'));
      return;
    }

    try {
      setAddingUser(true);
      const checkResponse = await fetch('/api/user-access/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const checkData = await checkResponse.json();
      if (!checkResponse.ok || !checkData.success) throw new Error('User check failed');
      if (!checkData.user) {
        toast.error(
          t(
            'admin.admins.errors.userNotFound',
            'User not found. They must sign in at least once before becoming an admin.'
          )
        );
        return;
      }

      const response = await fetch('/api/user-access/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, can_access: true })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error('Grant failed');

      toast.success(t('admin.admins.success.granted', { email }));
      setNewEmail('');
      setShowSuggestions(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast.error(t('admin.admins.errors.grant', 'Failed to grant admin access'));
    } finally {
      setAddingUser(false);
    }
  };

  const runPendingAction = async () => {
    if (!pendingAction) return;
    const { email, type } = pendingAction;

    try {
      setBusyEmail(email);
      const endpoint = type === 'revoke' ? '/api/user-access/update' : '/api/user-access/ban';
      const body = type === 'revoke'
        ? { email, can_access: false }
        : { email, banned: type === 'ban' };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.detail || 'Update failed');

      if (type === 'revoke') toast.success(t('admin.admins.success.revoked', { email }));
      else if (type === 'ban') toast.success(t('admin.admins.bans.banSuccess', { email }));
      else toast.success(t('admin.admins.bans.unbanSuccess', { email }));

      setBanSearch('');
      setPendingAction(null);
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user access:', error);
      toast.error(t('admin.admins.bans.banError', 'Failed to update access'));
    } finally {
      setBusyEmail(null);
    }
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString(
      i18n.language.toLowerCase().startsWith('sr') ? 'sr-RS' : 'en-GB',
      { day: '2-digit', month: 'short', year: 'numeric' }
    );
  };

  const confirmationCopy = pendingAction?.type === 'revoke'
    ? {
        title: t('admin.admins.confirm.revokeTitle', 'Remove admin access?'),
        description: t('admin.admins.confirm.revokeDescription', { email: pendingAction.email }),
        action: t('admin.admins.activeAdmins.revokeButton', 'Remove access'),
        destructive: true
      }
    : pendingAction?.type === 'ban'
      ? {
          title: t('admin.admins.confirm.banTitle', 'Ban this user?'),
          description: t('admin.admins.bans.confirmBan', { email: pendingAction.email }),
          action: t('admin.admins.bans.banButton', 'Ban'),
          destructive: true
        }
      : {
          title: t('admin.admins.confirm.unbanTitle', 'Restore this account?'),
          description: t('admin.admins.bans.confirmUnban', { email: pendingAction?.email }),
          action: t('admin.admins.bans.unbanButton', 'Unban'),
          destructive: false
        };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">{t('admin.admins.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('admin.admins.description')}</p>
      </div>

      <Tabs defaultValue="admins">
        <TabsList className="grid h-10 w-full max-w-md grid-cols-2">
          <TabsTrigger value="admins" className="gap-2">
            {t('admin.admins.activeAdmins.title')}
            <span className="text-xs tabular-nums text-muted-foreground">{activeAdmins.length}</span>
          </TabsTrigger>
          <TabsTrigger value="banned" className="gap-2">
            {t('admin.admins.bans.bannedTitle')}
            <span className="text-xs tabular-nums text-muted-foreground">{bannedUsers.length}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="admins" className="mt-4">
          <section className="overflow-visible rounded-xl border border-border bg-card">
          <div className="border-b border-border px-5 py-4">
            <h2 className="font-semibold">{t('admin.admins.addNew.title')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('admin.admins.addNew.description')}</p>
            <form onSubmit={addAdmin} className="mt-4 flex max-w-2xl gap-2">
              <div className="relative min-w-0 flex-1">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  type="email"
                  placeholder={t('admin.admins.addNew.placeholder')}
                  value={newEmail}
                  onChange={(event) => {
                    setNewEmail(event.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  disabled={addingUser}
                  autoComplete="off"
                  className="pl-9"
                />

                {showSuggestions && adminSuggestions.length > 0 ? (
                  <div ref={dropdownRef} className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
                    {adminSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.id}
                        type="button"
                        onClick={() => {
                          setNewEmail(suggestion.email);
                          setShowSuggestions(false);
                        }}
                        className="fongpt-hover flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm"
                      >
                        <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">{suggestion.email}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <Button type="submit" disabled={addingUser || !newEmail.trim()}>
                {addingUser ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
                {addingUser ? t('admin.admins.addNew.adding') : t('admin.admins.addNew.button')}
              </Button>
            </form>
          </div>

          <div className="flex items-center justify-between gap-3 px-5 py-3">
            <div>
              <h2 className="font-semibold">{t('admin.admins.activeAdmins.title')}</h2>
              <p className="text-sm text-muted-foreground">{t('admin.admins.activeAdmins.description')}</p>
            </div>
            <span className="text-sm tabular-nums text-muted-foreground">{activeAdmins.length}</span>
          </div>

          <div className="overflow-x-auto px-5 pb-4">
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-14 w-full" />)}
              </div>
            ) : activeAdmins.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('admin.admins.activeAdmins.noAdmins')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.admins.table.account', 'Account')}</TableHead>
                    <TableHead>{t('admin.admins.table.role', 'Role')}</TableHead>
                    <TableHead className="text-right">{t('admin.admins.table.actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeAdmins.map((admin) => {
                    const isCurrentUser = currentEmail === admin.email.toLowerCase();
                    return (
                      <TableRow key={admin.id}>
                        <TableCell className="py-4 font-medium">{admin.email}</TableCell>
                        <TableCell className="py-4">
                          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                            <span className="h-2 w-2 rounded-full bg-[#56c0a9]" />
                            {t('admin.admins.suggestions.admin')}
                            {isCurrentUser ? ` · ${t('admin.admins.activeAdmins.you')}` : ''}
                          </span>
                        </TableCell>
                        <TableCell className="py-4 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPendingAction({ email: admin.email, type: 'revoke' })}
                            disabled={isCurrentUser || busyEmail === admin.email}
                            title={isCurrentUser ? t('admin.admins.activeAdmins.cannotRevokeSelf') : t('admin.admins.activeAdmins.revokeTitle')}
                          >
                            <UserMinus className="mr-2 h-4 w-4" />
                            {t('admin.admins.activeAdmins.revokeButton')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
          </section>
        </TabsContent>

        <TabsContent value="banned" className="mt-4">
          <section className="overflow-visible rounded-xl border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
            <div>
              <h2 className="font-semibold">{t('admin.admins.bans.title')}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t('admin.admins.bans.description')}</p>
            </div>
            <Button variant="destructive" onClick={() => setBanDialogOpen(true)}>
              <Ban className="mr-2 h-4 w-4" />
              {t('admin.admins.bans.addButton', 'Ban user')}
            </Button>
          </div>

          <div className="flex items-center justify-between gap-3 px-5 py-3">
            <h2 className="font-semibold">{t('admin.admins.bans.bannedTitle')}</h2>
            <span className="text-sm tabular-nums text-muted-foreground">{bannedUsers.length}</span>
          </div>

          <div className="overflow-x-auto px-5 pb-4">
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 2 }).map((_, index) => <Skeleton key={index} className="h-14 w-full" />)}
              </div>
            ) : bannedUsers.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('admin.admins.bans.noBanned')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.admins.table.account', 'Account')}</TableHead>
                    <TableHead>{t('admin.admins.table.bannedOn', 'Banned on')}</TableHead>
                    <TableHead className="text-right">{t('admin.admins.table.actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bannedUsers.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="py-4 font-medium">{entry.email}</TableCell>
                      <TableCell className="py-4 text-sm text-muted-foreground">
                        <p>{formatDate(entry.banned_at)}</p>
                        {entry.banned_by ? (
                          <p className="mt-0.5 max-w-[220px] truncate text-xs" title={entry.banned_by}>
                            {t('admin.admins.table.by', { name: entry.banned_by })}
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        <Button variant="outline" size="sm" onClick={() => setPendingAction({ email: entry.email, type: 'unban' })} disabled={busyEmail === entry.email}>
                          <RotateCcw className="mr-2 h-4 w-4" />
                          {t('admin.admins.bans.unbanButton')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          </section>
        </TabsContent>
      </Tabs>

      <Dialog
        open={banDialogOpen}
        onOpenChange={(open) => {
          setBanDialogOpen(open);
          if (!open) setBanSearch('');
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('admin.admins.bans.addTitle', 'Ban a user')}</DialogTitle>
            <DialogDescription>
              {t(
                'admin.admins.bans.addDescription',
                'Find an account by email. Banning immediately blocks sign-in.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={banSearch}
              onChange={(event) => setBanSearch(event.target.value)}
              placeholder={t('admin.admins.bans.searchPlaceholder')}
              autoComplete="off"
              autoFocus
              className="pl-9"
            />
          </div>
          <div className="min-h-24 overflow-hidden rounded-lg border border-border">
            {!banSearch.trim() ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t('admin.admins.bans.searchHint', 'Start typing an email address.')}
              </p>
            ) : banSearchResults.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t('admin.admins.bans.noResults')}
              </p>
            ) : (
              banSearchResults.map((entry) => {
                const isCurrentUser = currentEmail === entry.email.toLowerCase();
                return (
                  <div key={entry.id} className="flex items-center justify-between gap-3 border-b border-border px-3 py-2.5 last:border-b-0">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{entry.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {entry.can_access ? t('admin.admins.suggestions.admin') : t('admin.admins.suggestions.user')}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        setBanDialogOpen(false);
                        setPendingAction({ email: entry.email, type: 'ban' });
                      }}
                      disabled={isCurrentUser}
                      title={isCurrentUser ? t('admin.admins.bans.cannotBanSelf') : t('admin.admins.bans.banTitle')}
                    >
                      {t('admin.admins.bans.banButton')}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(pendingAction)} onOpenChange={(open) => !open && setPendingAction(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmationCopy.title}</DialogTitle>
            <DialogDescription>{confirmationCopy.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAction(null)} disabled={Boolean(busyEmail)}>
              {t('admin.admins.confirm.cancel', 'Cancel')}
            </Button>
            <Button variant={confirmationCopy.destructive ? 'destructive' : 'default'} onClick={runPendingAction} disabled={Boolean(busyEmail)}>
              {busyEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : confirmationCopy.destructive ? <Ban className="mr-2 h-4 w-4" /> : <Shield className="mr-2 h-4 w-4" />}
              {confirmationCopy.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUserManagement;
